import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { CookieConsent } from "./components/CookieConsent";
import { GoogleAnalytics } from "./components/GoogleAnalytics";
import { MetaPixel } from "./components/MetaPixel";
import { HomePageStructuredData } from "./components/StructuredData";
import { SiteContentProvider } from "./context/SiteContentContext";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import FleetPage from "./pages/FleetPage";
import DestinationsPage from "./pages/DestinationsPage";
import WhyChooseUsPage from "./pages/WhyChooseUsPage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import CareersPage from "./pages/CareersPage";
import NewsPage from "./pages/NewsPage";
import AdminPage from "./pages/AdminPage";
import LegalPage from "./pages/LegalPage";
import LdmCalculatorPage from "./pages/LdmCalculatorPage";
import NotFoundPage from "./pages/NotFoundPage";
import { ChevronUp } from "lucide-react";

// Page transition wrapper
function PageTransition({ children }) {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <div 
      className={`transition-all duration-500 ease-out ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-4'
      }`}
    >
      {children}
    </div>
  );
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);
  
  return null;
}

// Back to top button
function BackToTopButton() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <button
      onClick={scrollToTop}
      data-testid="back-to-top-btn"
      className={`fixed bottom-24 right-6 z-50 w-12 h-12 bg-[#C41E3A] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#A01830] transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      aria-label="Back to top"
    >
      <ChevronUp className="w-6 h-6" />
    </button>
  );
}

// Routes component that contains all public pages
const PublicRoutes = () => (
  <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/about" element={<AboutPage />} />
    <Route path="/services" element={<ServicesPage />} />
    <Route path="/fleet" element={<FleetPage />} />
    <Route path="/destinations" element={<DestinationsPage />} />
    <Route path="/why-choose-us" element={<WhyChooseUsPage />} />
    <Route path="/careers" element={<CareersPage />} />
    <Route path="/news" element={<NewsPage />} />
    <Route path="/news/:slug" element={<NewsPage />} />
    <Route path="/faq" element={<FAQPage />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="/ldm-calculator" element={<LdmCalculatorPage />} />
    <Route path="/legal/:pageId" element={<LegalPage />} />
    <Route path="/404" element={<NotFoundPage />} />
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);

// Layout with Header and Footer
const PublicLayout = () => (
  <>
    <Header />
    <main>
      <PageTransition>
        <PublicRoutes />
      </PageTransition>
    </main>
    <Footer />
  </>
);

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <SiteContentProvider>
          <div className="App min-h-screen bg-slate-50">
            <ScrollToTop />
            <BackToTopButton />
            <CookieConsent />
            <GoogleAnalytics />
            <MetaPixel />
            <HomePageStructuredData />
            <Routes>
              {/* Admin route without header/footer */}
              <Route path="/admin" element={<AdminPage />} />
            
              {/* English routes with /en prefix */}
              <Route path="/en/*" element={<PublicLayout />} />
              <Route path="/en" element={<PublicLayout />} />
            
              {/* Bulgarian routes (default, no prefix) */}
              <Route path="/*" element={<PublicLayout />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </div>
        </SiteContentProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
